import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { DealerProvider } from '../../../../../../auth/dealer/dealer.provider';
import { normalizeProvinceCode } from '../../../../../utils/province-code';
import {
  CustomerAddress,
  CustomerContact,
  CustomerMainType,
  CustomerSalutation,
  CustomerTitle,
  MainAddress,
  NationalIdDocumentType,
  VatGroup,
} from '../../../../model/customer-information.model';
import { CustomerVehicle, CustomerVehicleRelationType } from '../../../../model/customer-vehicle.model';
import {
  RetailCrmAccountConnection,
  RetailCrmAccountDetailsAddress,
  RetailCrmAccountOku,
  RetailCrmAccountVehicleInformation,
  RetailCrmMainAddress,
  RetailCrmVatGroup,
} from '../../model/responses/account-details.model';
import {
  RetailCrmCustomerMainType,
  RetailCrmResponseSalutation,
  RetailCrmResponseTitle,
} from '../../model/responses/get-accounts.model';
import { RetailCrmNationalIdDocumentType } from '../../model/responses/national-id-document-type.model';

@Injectable({
  providedIn: 'root',
})
export class RetailCrmCustomerFieldsViewModelMapper {
  private readonly logger = inject(NGXLogger);
  private readonly dealerProvider = inject(DealerProvider);

  private static readonly GbIeMarkets = new Set(['GB', 'IE']);

  private isGbOrIeMarket(): boolean {
    const country = this.dealerProvider.activeCountry();
    return !!country && RetailCrmCustomerFieldsViewModelMapper.GbIeMarkets.has(country.toUpperCase());
  }

  mapSalutation(salutation: RetailCrmResponseSalutation | undefined): CustomerSalutation | undefined {
    return this.isGbOrIeMarket() ? this.mapGbIeSalutation(salutation) : this.mapDefaultSalutation(salutation);
  }

  private mapDefaultSalutation(salutation: RetailCrmResponseSalutation | undefined): CustomerSalutation | undefined {
    switch (salutation) {
      case RetailCrmResponseSalutation.MR:
        return CustomerSalutation.MR;
      case RetailCrmResponseSalutation.MRS: // ANFRA maps to Ms in non-GB/IE markets
      case RetailCrmResponseSalutation.MISS: // ANMIS maps to Ms in non-GB/IE markets
        return CustomerSalutation.MS;
      case RetailCrmResponseSalutation.MX:
        return CustomerSalutation.MX;
      default:
        return undefined;
    }
  }

  private mapGbIeSalutation(salutation: RetailCrmResponseSalutation | undefined): CustomerSalutation | undefined {
    switch (salutation) {
      case RetailCrmResponseSalutation.DR:
        return CustomerSalutation.DR;
      case RetailCrmResponseSalutation.MISS:
        return CustomerSalutation.MISS;
      case RetailCrmResponseSalutation.MR:
        return CustomerSalutation.MR;
      case RetailCrmResponseSalutation.MRS: // ANFRA maps to Mrs in GB/IE markets
        return CustomerSalutation.MRS;
      case RetailCrmResponseSalutation.MS:
        return CustomerSalutation.MS;
      case RetailCrmResponseSalutation.MX:
        return CustomerSalutation.MX;
      default:
        return undefined;
    }
  }

  mapTitle(title: RetailCrmResponseTitle | undefined): CustomerTitle | undefined {
    switch (title) {
      case RetailCrmResponseTitle.DR:
        return CustomerTitle.DR;
      case RetailCrmResponseTitle.PROF:
        return CustomerTitle.PROF;
      case RetailCrmResponseTitle.PROF_DR:
        return CustomerTitle.PROF_DR;
      default:
        return undefined;
    }
  }

  mapAddress(address: RetailCrmAccountDetailsAddress | undefined): CustomerAddress | undefined {
    if (!address) {
      return undefined;
    }
    return {
      houseNumber: address.addressStreetNumber,
      street: address.addressStreet,
      city: address.addressCity,
      postalCode: address.addressPostalCode,
      country: address.addressCountry,
      addressLine1: address.addressComplement,
      addressLine2: address.addressLine2,
      province: normalizeProvinceCode(address.addressState, address.addressCountry),
      postBox: address.addressPostBox,
    };
  }

  mapMainAddress(retailMainAddress: RetailCrmMainAddress | undefined): MainAddress | undefined {
    switch (retailMainAddress) {
      case RetailCrmMainAddress.BUSINESS:
        return MainAddress.BUSINESS;
      case RetailCrmMainAddress.PRIVATE:
        return MainAddress.PRIVATE;
      default:
        return undefined;
    }
  }

  mapContact(
    address: RetailCrmAccountDetailsAddress | undefined,
    connection: RetailCrmAccountConnection,
    type: 'private' | 'business',
  ): CustomerContact {
    return {
      address: this.mapAddress(address),
      email: type === 'private' ? connection?.emailPrivate : connection.emailBusiness,
      phone: type === 'private' ? connection?.phonePrivate : connection.phoneBusiness,
      mobile: type === 'private' ? connection?.mobilePrivate : connection.mobileBusiness,
    };
  }

  mapVehicles(vehicleInformation: RetailCrmAccountVehicleInformation[] | undefined): CustomerVehicle[] | undefined {
    if (!vehicleInformation) {
      return;
    }

    return vehicleInformation
      .filter(
        // Discard vehicles without VIN, as we cannot perform any operations on them
        (vehicleInfo): vehicleInfo is RetailCrmAccountVehicleInformation & Record<'VIN', string> => {
          if (vehicleInfo.VIN) {
            return true;
          }
          this.logger.warn('#CDFTF0247 Skipping vehicle without VIN when parsing Retail CRM customer', { vehicleInfo });
          return false;
        },
      )
      .map(
        (vehicleInfo): CustomerVehicle => ({
          name: vehicleInfo.name,
          vin: vehicleInfo.VIN,
          licensePlate: vehicleInfo.registrationNumber,
          relationTypes: this.mapVehicleRelations(vehicleInfo.oku),
        }),
      );
  }

  mapVehicleRelations(oku: RetailCrmAccountOku): CustomerVehicleRelationType[] {
    const okus = [];

    if (oku.owner) {
      okus.push(CustomerVehicleRelationType.OWNER);
    }
    if (oku.keeper) {
      okus.push(CustomerVehicleRelationType.KEEPER);
    }
    if (oku.user) {
      okus.push(CustomerVehicleRelationType.USER);
    }

    return okus;
  }

  mapNationalIdDocumentType(
    retailDocumentType: RetailCrmNationalIdDocumentType | undefined,
  ): NationalIdDocumentType | undefined {
    switch (retailDocumentType) {
      case RetailCrmNationalIdDocumentType.DRIVER_LICENSE:
        return NationalIdDocumentType.DRIVER_LICENCE;
      case RetailCrmNationalIdDocumentType.DRIVER_LICENSE_ID:
        return NationalIdDocumentType.DRIVER_LICENSE_ID;
      case RetailCrmNationalIdDocumentType.ID_CARD:
        return NationalIdDocumentType.IDENTITY_CARD;
      case RetailCrmNationalIdDocumentType.IDENTIFICATION_NUMBER:
        return NationalIdDocumentType.IDENTIFICATION_NUMBER;
      case RetailCrmNationalIdDocumentType.PASSPORT:
        return NationalIdDocumentType.PASSPORT;
      case RetailCrmNationalIdDocumentType.PASSPORT_ID:
        return NationalIdDocumentType.PASSPORT_ID;
      case RetailCrmNationalIdDocumentType.PERSON_ID_CARD:
        return NationalIdDocumentType.PERSON_ID;
      case RetailCrmNationalIdDocumentType.SOCIAL_SECURITY_NUMBER:
        return NationalIdDocumentType.SOCIAL_SECURITY_NUMBER;
      case RetailCrmNationalIdDocumentType.SOUTH_AFRICA_NATIONAL_ID:
        return NationalIdDocumentType.SOUTH_AFRICA_NATIONAL_ID;
      default:
        return undefined;
    }
  }

  mapVatGroup(retailVatGroup: RetailCrmVatGroup | undefined): VatGroup | undefined {
    switch (retailVatGroup) {
      case RetailCrmVatGroup.NONE:
        return VatGroup.NONE;
      case RetailCrmVatGroup.REDUCED:
        return VatGroup.REDUCED;
      case RetailCrmVatGroup.STANDARD:
        return VatGroup.STANDARD;
      default:
        return undefined;
    }
  }
  mapCustomerMainType(retailCustomerType: RetailCrmCustomerMainType | undefined): CustomerMainType | undefined {
    switch (retailCustomerType) {
      case RetailCrmCustomerMainType.ORGANIZATION:
        return CustomerMainType.ORGANIZATION;
      case RetailCrmCustomerMainType.SELF_EMPLOYED:
        return CustomerMainType.SELF_EMPLOYED;
      case RetailCrmCustomerMainType.INDIVIDUAL:
        return CustomerMainType.INDIVIDUAL;
      case RetailCrmCustomerMainType.DEALER:
        return CustomerMainType.DEALER;
      default:
        return undefined;
    }
  }
}
